import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ORPCError } from '@orpc/client';
import { T, useGT, Var } from 'gt-react';
import { AlarmIcon } from '@phosphor-icons/react';
import {
  SNOOZE_CONFIRMATION_REQUIRED,
  type TaskSnoozePreset,
} from '@erp/shared-types';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { toast } from 'src/components/ui/toast';
import { snoozePartialSuccessMessage } from './snoozeMessages';
import {
  fromDateTimeLocalValue,
  isSnoozeInstantInPast,
  snoozePresetUntil,
  toDateTimeLocalValue,
} from './taskSnooze';

const PRESETS = ['tomorrow', 'three_days', 'one_week'] as const;

type SnoozeWarnings = { subtasks: boolean; pastDue: boolean };

function useSnoozeFormState() {
  const [preset, setPreset] = useState<TaskSnoozePreset>('tomorrow');
  const [specific, setSpecific] = useState(() =>
    toDateTimeLocalValue(snoozePresetUntil('tomorrow')),
  );
  const [note, setNote] = useState('');
  const [clearsOnEdit, setClearsOnEdit] = useState(true);
  const [confirmSubtasks, setConfirmSubtasks] = useState(false);
  const [confirmPastDue, setConfirmPastDue] = useState(false);
  const [warnings, setWarnings] = useState<SnoozeWarnings | null>(null);
  const [untilIsPast, setUntilIsPast] = useState(false);

  function resetWarnings() {
    setWarnings(null);
    setConfirmSubtasks(false);
    setConfirmPastDue(false);
  }

  const until =
    preset === 'specific'
      ? fromDateTimeLocalValue(specific)
      : snoozePresetUntil(preset);

  return {
    preset,
    setPreset,
    specific,
    setSpecific,
    note,
    setNote,
    clearsOnEdit,
    setClearsOnEdit,
    confirmSubtasks,
    confirmPastDue,
    warnings,
    setWarnings,
    setConfirmSubtasks,
    setConfirmPastDue,
    resetWarnings,
    until,
    untilIsPast,
    setUntilIsPast,
  };
}

/**
 * Snooze one task or a selection. Presets are 9:00 in the browser's timezone.
 * A specific choice is a date and time, defaulting to 9:00 tomorrow. The
 * server rejects a time in the past and asks for confirmation when the task
 * has subtasks or the snooze outlasts the due date.
 */
export function SnoozeTaskDialog({
  taskIds,
  open,
  onOpenChange,
}: {
  taskIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const form = useSnoozeFormState();
  const {
    preset,
    setPreset,
    specific,
    setSpecific,
    note,
    setNote,
    clearsOnEdit,
    setClearsOnEdit,
    confirmSubtasks,
    setConfirmSubtasks,
    confirmPastDue,
    setConfirmPastDue,
    warnings,
    setWarnings,
    resetWarnings,
    until,
    untilIsPast,
    setUntilIsPast,
  } = form;

  const snooze = useMutation({
    mutationFn: (confirms: { subtasks: boolean; pastDue: boolean }) => {
      if (!until) {
        throw new Error(
          gt('Choose a date and time', {
            context: 'snooze dialog validation when the specific date is empty',
          }),
        );
      }
      return rpcClient.tasks.snooze({
        taskIds,
        until,
        preset,
        note: note.trim() || undefined,
        clearsOnEdit,
        confirmSubtasks: confirms.subtasks,
        confirmPastDue: confirms.pastDue,
      });
    },
    onSuccess: (result) => {
      invalidateTaskQueries(queryClient);
      const skipped = result.skipped.length;
      toast.success(
        skipped > 0
          ? snoozePartialSuccessMessage(gt, result.tasks.length, skipped)
          : gt('Task snoozed', {
              context: 'toast after snoozing a task',
            }),
      );
      onOpenChange(false);
      resetWarnings();
    },
    onError: (error) => {
      const confirmation = snoozeConfirmationFromError(error);
      if (confirmation) {
        setWarnings(confirmation);
        setConfirmSubtasks(confirmation.subtasks);
        setConfirmPastDue(confirmation.pastDue);
        return;
      }
      toast.error(snoozeFailureMessage(error, gt));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="title of the snooze task dialog">Snooze</T>
          </DialogTitle>
          <DialogDescription>
            {taskIds.length === 1 ? (
              <T context="explanation of snoozing one task">
                Hide this task from task lists until the time you pick. Opening
                it directly still works.
              </T>
            ) : (
              <T context="explanation of snoozing several tasks">
                Hide these tasks from task lists until the time you pick.
                Opening one directly still works.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>
        <SnoozeDialogOptions
          preset={preset}
          specific={specific}
          note={note}
          clearsOnEdit={clearsOnEdit}
          warnings={warnings}
          untilIsPast={untilIsPast}
          onPreset={(next) => {
            setPreset(next);
            setUntilIsPast(false);
            resetWarnings();
          }}
          onSpecific={(value) => {
            setSpecific(value);
            // Presets are always in the future. A specific choice is checked
            // here, not during render, so the clock stays out of render.
            setUntilIsPast(
              isSnoozeInstantInPast(fromDateTimeLocalValue(value), new Date()),
            );
            resetWarnings();
          }}
          onNote={setNote}
          onClearsOnEdit={setClearsOnEdit}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <T context="cancel the snooze dialog">Cancel</T>
          </Button>
          <Button
            type="button"
            disabled={snooze.isPending || until == null || untilIsPast}
            onClick={() =>
              snooze.mutate({
                subtasks: confirmSubtasks,
                pastDue: confirmPastDue,
              })
            }
          >
            <AlarmIcon className="size-4" />
            {warnings ? (
              <T context="confirm snooze after the warning">Confirm Snooze</T>
            ) : taskIds.length > 1 ? (
              <T context="submit snooze for several selected tasks">
                Snooze <Var>{taskIds.length}</Var>
              </T>
            ) : (
              <T context="submit the snooze dialog">Snooze</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnoozeDialogOptions({
  preset,
  specific,
  note,
  clearsOnEdit,
  warnings,
  untilIsPast,
  onPreset,
  onSpecific,
  onNote,
  onClearsOnEdit,
}: {
  preset: TaskSnoozePreset;
  specific: string;
  note: string;
  clearsOnEdit: boolean;
  warnings: SnoozeWarnings | null;
  untilIsPast: boolean;
  onPreset: (preset: TaskSnoozePreset) => void;
  onSpecific: (value: string) => void;
  onNote: (value: string) => void;
  onClearsOnEdit: (value: boolean) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((item) => (
          <Button
            key={item}
            type="button"
            size="sm"
            variant={preset === item ? 'default' : 'outline'}
            onClick={() => onPreset(item)}
          >
            {presetLabel(gt, item)}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant={preset === 'specific' ? 'default' : 'outline'}
          onClick={() => onPreset('specific')}
        >
          <T context="snooze preset that opens a date and time picker">
            Specific Date
          </T>
        </Button>
      </div>
      {preset === 'specific' && (
        <div className="flex flex-col gap-1">
          <Label htmlFor="snooze-until">
            <T context="label for the snooze date and time input">
              Snooze Until
            </T>
          </Label>
          <Input
            id="snooze-until"
            type="datetime-local"
            value={specific}
            onChange={(event) => onSpecific(event.target.value)}
          />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Label htmlFor="snooze-note">
          <T context="label for the optional snooze note">Note</T>
        </Label>
        <Textarea
          id="snooze-note"
          value={note}
          rows={2}
          placeholder={gt('Optional', {
            context: 'placeholder for the optional snooze note',
          })}
          onChange={(event) => onNote(event.target.value)}
        />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox
          checked={clearsOnEdit}
          onCheckedChange={(checked) => onClearsOnEdit(checked === true)}
        />
        <span>
          <T context="checkbox: editing the task clears this snooze. Default on.">
            Clear this snooze if the task is edited
          </T>
        </span>
      </label>
      <SnoozeDialogWarnings warnings={warnings} untilIsPast={untilIsPast} />
    </div>
  );
}

function SnoozeDialogWarnings({
  warnings,
  untilIsPast,
}: {
  warnings: SnoozeWarnings | null;
  untilIsPast: boolean;
}) {
  return (
    <>
      {warnings && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {warnings.subtasks && (
            <p>
              <T context="warning that snoozing a parent does not hide its subtasks">
                This hides only the selected task. Its subtasks stay in their
                lists.
              </T>
            </p>
          )}
          {warnings.pastDue && (
            <p>
              <T context="warning that the snooze lasts past the task due date">
                This stays hidden past the due date. The due date does not
                change.
              </T>
            </p>
          )}
        </div>
      )}
      {untilIsPast && (
        <p className="text-sm text-destructive">
          <T context="validation: snooze time is already in the past">
            Choose a time in the future.
          </T>
        </p>
      )}
    </>
  );
}

function presetLabel(
  gt: ReturnType<typeof useGT>,
  preset: 'tomorrow' | 'three_days' | 'one_week',
): string {
  if (preset === 'tomorrow') {
    return gt('Tomorrow', { context: 'snooze preset, 9:00 tomorrow morning' });
  }
  if (preset === 'three_days') {
    return gt('3 Days', { context: 'snooze preset, 9:00 three days from now' });
  }
  return gt('1 Week', { context: 'snooze preset, 9:00 one week from now' });
}

function snoozeConfirmationFromError(
  error: unknown,
): { subtasks: boolean; pastDue: boolean } | null {
  if (!(error instanceof ORPCError) || error.code !== 'PRECONDITION_FAILED') {
    return null;
  }
  if (!isConfirmation(error.data)) return null;
  return { subtasks: error.data.subtasks, pastDue: error.data.pastDue };
}

function snoozeFailureMessage(
  error: unknown,
  gt: ReturnType<typeof useGT>,
): string {
  if (error instanceof Error) return error.message;
  return gt('Could not snooze', {
    context: 'toast after a task snooze failed',
  });
}

function isConfirmation(
  data: unknown,
): data is { code: string; subtasks: boolean; pastDue: boolean } {
  if (typeof data !== 'object' || data == null) return false;
  if (!('code' in data) || !('subtasks' in data) || !('pastDue' in data)) {
    return false;
  }
  return data.code === SNOOZE_CONFIRMATION_REQUIRED;
}
